import type { UserFilters } from '../types'

// ─── Session Storage ──────────────────────────────────────────────────────────

export const SESSION_FILTER_KEY = 'userlist_filter'

// ─── Pagination ───────────────────────────────────────────────────────────────

export const PER_PAGE_OPTIONS = [15, 30, 50, 100] as const

export const DEFAULT_FILTERS: UserFilters = {
  page: 1,
  perPage: 15,
  sortBy: 'asc',
  orderBy: 'name',
}

// ─── Dropdown Options ────────────────────────────────────────────────────────

export const ORGANIZATION_TYPE_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'PMC', label: 'Property Management Company' },
  { value: 'LIBERTY', label: 'Liberty' },
  { value: 'CHARITY', label: 'Charity' },
  { value: 'RESIDENT', label: 'Applicants/Residents' },
] as const

export const ACCOUNT_STATUS_OPTIONS = [
  { value: '', label: 'All User Statuses' },
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
  { value: 'Locked', label: 'Locked' },
] as const

export const PROPERTY_STATUS_OPTIONS = [
  { value: '', label: 'All Property Statuses' },
  { value: '1', label: 'Active' },
  { value: '0', label: 'Not Active' },
] as const

// ─── Role Protection ──────────────────────────────────────────────────────────

/** Display names used in rolename[] arrays from API responses */
export const PROTECTED_ROLES_DISPLAY = ['Applicant', 'Resident', 'Past Resident'] as const

/** Angular role keys for role_name field */
export const PROTECTED_ROLE_KEYS = ['ROLE_RENTER', 'ROLE_RESIDENT', 'ROLE_PAST_RESIDENT'] as const

/** Grid view only checks these two — replicates Angular bug exactly */
export const PROTECTED_ROLES_GRID_VIEW = ['Applicant', 'Resident'] as const

// ─── Finance Manager Eligible Roles ───────────────────────────────────────────

export const FIN_MGR_ELIGIBLE_ROLES = [
  'ROLE_COMPANY_ADMIN',
  'ROLE_COMPANY_MANAGER',
  'ROLE_PROPERTY_ADMIN',
  'ROLE_PROPERTY_MANAGER',
] as const

// ─── Liberty Roles ────────────────────────────────────────────────────────────

export const LIBERTY_ROLE_KEYS = [
  'ROLE_ADMIN',
  'ROLE_EXECS',
  'ROLE_CUSTOMER_SERVICE',
  'ROLE_CUSTOMER_SERVICE_MANAGER',
  'ROLE_LIBERTY_DEFAULTS',
  'ROLE_LIBERTY_SALES',
  'ROLE_UNDERWRITER',
  'ROLE_LIBERTY_FIN_MGR',
  'EXTERNAL_INSURANCE',
  'ROLE_LIBERTY_ACCOUNTING',
  'ROLE_REFUND_APPROVAL',
] as const

// ─── Role Name → Display Name Map (for Assign Role page) ─────────────────────

export const PROPERTY_STAFF_ROLES: Record<string, string> = {
  ROLE_PROPERTY_MANAGER: 'Property Manager',
  ROLE_PROPERTY_AGENT: 'Leasing Consultant',
  ROLE_PROPERTY_ADMIN: 'Property Assistant',
}

export const PROPERTY_ROLES_WITH_REGIONAL: Record<string, string> = {
  ROLE_PROPERTY_MANAGER: 'Property Manager',
  ROLE_PROPERTY_AGENT: 'Leasing Consultant',
  ROLE_PROPERTY_ADMIN: 'Property Assistant',
  ROLE_REGIONAL_MANAGER: 'Company Regional Manager',
}

export const COMPANY_ROLES: Record<string, string> = {
  ROLE_COMPANY_MANAGER: 'Company Manager',
  ROLE_COMPANY_ADMIN: 'Company Admin',
  ROLE_REGIONAL_MANAGER: 'Company Regional Manager',
}

/** Role key → display name for the Edit User roles table (falls back to formatRoleKey). */
export const ROLE_DISPLAY_MAP: Record<string, string> = {
  ROLE_PROPERTY_MANAGER: 'Property Manager',
  ROLE_PROPERTY_AGENT: 'Leasing Consultant',
  ROLE_PROPERTY_ADMIN: 'Property Assistant',
  ROLE_COMPANY_MANAGER: 'Company Manager',
  ROLE_COMPANY_ADMIN: 'Company Admin',
  ROLE_REGIONAL_MANAGER: 'Regional Manager',
  ROLE_RENTER: 'Resident',
  ROLE_APPLICANT: 'Applicant',
  ROLE_ATLANTA_OPEN_DOOR_ADMIN: 'Atlanta Open Door Admin',
  ROLE_ATLANTA_OPEN_DOOR_MANAGER: 'Atlanta Open Door Manager',

  // Liberty Internal Roles
  EXTERNAL_INSURANCE: 'External Insurance',
  ROLE_ADMIN: 'Liberty Admin',
  ROLE_CUSTOMER_SERVICE: 'Liberty Customer Service',
  ROLE_CUSTOMER_SERVICE_MANAGER: 'Liberty Customer Service Manager',
  ROLE_EXECS: 'Liberty Execs',
  ROLE_LIBERTY_ACCOUNTING: 'Liberty Accounting',
  ROLE_LIBERTY_DEFAULTS: 'Liberty Defaults',
  ROLE_LIBERTY_FIN_MGR: 'Liberty Finance Manager',
  ROLE_LIBERTY_SALES: 'Liberty Sales',
  ROLE_UNDERWRITER: 'Liberty Underwriter',
  ROLE_REFUND_APPROVAL: 'Liberty Refund Approval',
}

// ─── Policies ─────────────────────────────────────────────────────────────────

export const USER_MANAGEMENT_POLICIES = {
  ADD_USER: 'user_management_add_user',
  EDIT_USER: 'user_management_edit_user',
  DELETE_USER: 'user_management_delete_user',
  ACTION_COLUMN: 'user_management_list_action_column',
  SELECT_ALL: 'user_management_list_select_all_user',
  SELECT_USER: 'user_management_list_select_user',
  MULTI_DELETE: 'user_management_list_multi_delete',
  EMAIL_BLAST: 'user_management_list_email_blast',
  EXPORT: 'user_management_list_export',
  ALL_REF_FILTER: 'user_management_list_all_reference_filter',
  IMPERSONATE_APPLICANT: 'user_management_impersonate_applicant',
  IMPERSONATE_PMC: 'user_management_impersonate_pmc_role',
  TEMP_PASSWORD: 'user_management_edit_temp_pswd',
  DISABLE_ENABLE: 'user_management_edit_disable_enable',
  ADD_NOTES: 'user_management_edit_add_notes',
  SET_FINANCE_MGR: 'user_management_edit_set_finance_mgr',
  ROLE_REMOVE: 'user_management_edit_role_remove',
  ROLE_EDIT: 'user_management_edit_role_edit',
  SELF_ASSIGN_PROPS: 'user_management_edit_self_assign_properties',
} as const

// ─── Assign-role level groupings ────────────────────────────────────────────

export const FIN_MGR_ROLES = [
  'ROLE_COMPANY_ADMIN',
  'ROLE_COMPANY_MANAGER',
  'ROLE_PROPERTY_ADMIN',
  'ROLE_PROPERTY_MANAGER',
  'ROLE_REGIONAL_MANAGER',
] as const

export const COMPANY_LEVEL = ['ROLE_COMPANY_MANAGER', 'ROLE_COMPANY_ADMIN', 'ROLE_REGIONAL_MANAGER']
export const PROPERTY_LEVEL = ['ROLE_PROPERTY_MANAGER', 'ROLE_PROPERTY_AGENT', 'ROLE_PROPERTY_ADMIN']
